/**
 * Structured data for the Solair® screen systems on /exterior-vertical-shades.
 *
 * Emits an ItemList of three Product nodes (Vertical Curtain, Residential
 * Power Screen, Commercial Power Screen) so Google can surface rich product
 * results, plus a set of FAQ entries that get merged into the page's single
 * FAQPage node (never emit two FAQPage nodes on one URL).
 */

import { absUrl, canonicalUrl, SITE_URL, SITE_NAME } from "@/lib/seo";
import vcPorch from "@/assets/solair-vc-porch.jpg.asset.json";
import vcCableWall from "@/assets/solair-vc-cable-wall.jpg.asset.json";
import resLanai from "@/assets/solair-ps-res-lanai.jpg.asset.json";
import resSunset from "@/assets/solair-res-sunset-porch.jpg.asset.json";
import comPatio from "@/assets/solair-ps-com-patio.jpg.asset.json";
import comRestaurant from "@/assets/solair-ps-com-restaurant.jpg.asset.json";

const PAGE_PATH = "/exterior-vertical-shades";
const PAGE_URL = canonicalUrl(PAGE_PATH);

const SOLAIR_BRAND = { "@type": "Brand", name: "Solair® Shade Solutions" };

const SELLER = {
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: SITE_NAME,
  telephone: "+1-830-390-9588",
};

interface SolairProductInput {
  anchor: string;
  name: string;
  description: string;
  images: string[];
  warrantyYears: number;
  properties: { name: string; value: string }[];
}

const PRODUCTS: SolairProductInput[] = [
  {
    anchor: "solair-vertical-curtain",
    name: "Solair® Vertical Curtain",
    description:
      "Manual gear or motorized vertical curtain for porches, patios and pergolas. Cable-guided or free-hanging with an aluminum roller tube end bracket and cable guide, weighted bottom bar and Sunbrella®, Phifer® or Textilene® screen fabrics. Backed by a 5-year Solair® warranty and installed across Central and South Texas by Texas Made Shade.",
    images: [vcPorch.url, vcCableWall.url],
    warrantyYears: 5,
    properties: [
      { name: "Operation", value: "Manual gear standard; optional Somfy® motorization with handheld remote" },
      { name: "Retention System", value: "Free-hanging or stainless cable-guided" },
      { name: "Frame Colors", value: "White, Beige, Bronze" },
      { name: "Fabrics", value: "Sunbrella® solids and Phifer® or Textilene® solar mesh" },
    ],
  },
  {
    anchor: "solair-power-screen-home",
    name: "Solair® Power Screen for Your Home",
    description:
      "Motorized residential exterior vertical shade with SRS zippered side tracks or cable guides. Blocks up to 97% of UV, cuts glare and heat gain while keeping the view, and operates by remote from up to 65 ft. Backed by a 10-year Solair® warranty.",
    images: [resLanai.url, resSunset.url],
    warrantyYears: 10,
    properties: [
      { name: "Operation", value: "120V Somfy® tubular motor, remote, wall switch, sun/wind sensor and smart-home control" },
      { name: "Retention System", value: "SRS zippered side tracks or cable-guided" },
      { name: "Maximum Size", value: "Up to approximately 20 ft wide by 12 ft tall per unit" },
      { name: "Frame Colors", value: "11 powder-coat colors including Black, Bronze, Brown, Green, Gray, Silver, Tan, Navajo, Desert Sand, White and Bright White" },
    ],
  },
  {
    anchor: "solair-power-screen-business",
    name: "Solair® Power Screen for Business",
    description:
      "Heavy-duty commercial motorized drop screen for restaurant patios, bars, hotels and pergolas. SRS zippered or cable retention systems extend usable outdoor seating in sun, wind and rain, reduce cooling load and carry a 10-year Solair® warranty.",
    images: [comPatio.url, comRestaurant.url],
    warrantyYears: 10,
    properties: [
      { name: "Application", value: "Restaurant patios, bars, hotels, pergolas and commercial storefronts" },
      { name: "Retention System", value: "SRS zippered side tracks or cable-guided" },
      { name: "Operation", value: "Motorized with remote, wall switch, sensors and building-automation integration" },
      { name: "Frame Colors", value: "Full 11-color commercial powder-coat palette" },
    ],
  },
];

function productNode(p: SolairProductInput) {
  return {
    // Service, not Product: quote-priced systems, and Google's Product snippet
    // requires offers/review/aggregateRating.
    "@type": "Service",
    "@id": `${PAGE_URL}#${p.anchor}`,
    name: p.name,
    description: p.description,
    brand: SOLAIR_BRAND,
    serviceType: "Exterior Vertical Shades & Motorized Drop Screens",
    category: "Exterior Vertical Shades & Motorized Drop Screens",
    image: p.images.map((src) => absUrl(src)),
    url: `${PAGE_URL}#${p.anchor}`,

    additionalProperty: [
      ...p.properties.map((prop) => ({
        "@type": "PropertyValue",
        name: prop.name,
        value: prop.value,
      })),
      { "@type": "PropertyValue", name: "Warranty", value: `${p.warrantyYears}-year warranty` },
    ],
    // No `offers` node on purpose: these systems are quote-priced, and Google
    // flags an Offer without a concrete `price` as invalid.
  };
}

/** FAQ pairs specific to the Solair® screen systems — merge into the page FAQPage. */
export const SOLAIR_SYSTEM_FAQS: { q: string; a: string }[] = [
  {
    q: "What is the difference between the Solair® Vertical Curtain and the Solair® Power Screen?",
    a: "The Vertical Curtain is the value option: standard manual gear operation (motor optional), free-hanging or cable-guided, White/Beige/Bronze frames and a 5-year warranty. The Power Screen is the premium motorized system with SRS zippered side tracks, an 11-color frame palette, sensor and smart-home control and a 10-year warranty.",
  },
  {
    q: "What is the SRS retention system on a Solair® Power Screen?",
    a: "SRS is Solair®'s zippered side-track retention system. The screen edge rides in a sealed track so the fabric stays taut in wind, blocks insects and seals the opening — the best choice for screened porches and restaurant patios. Cable-guided retention is the lower-profile alternative for architectural openings.",
  },
  {
    q: "How long is the warranty on Solair® screen systems?",
    a: "Solair® Vertical Curtains carry a 5-year warranty. Both the Residential and Commercial Power Screens carry a 10-year warranty. As an authorized Solair® dealer, Texas Made Shade files warranty claims and sources genuine replacement parts and motors for you.",
  },
  {
    q: "What frame colors are available for Solair® screens?",
    a: "Vertical Curtains come in White, Beige and Bronze. Power Screens are available in 11 powder-coat colors: Black, Brown, Bronze, Green, Gray, Silver, Tan, Navajo, Desert Sand, White and Bright White.",
  },
];

/** JSON-LD script objects for the Solair® product line-up. */
export function solairSystemsScripts() {
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${PAGE_URL}#solair-systems`,
    name: "Solair® Exterior Screen Systems",
    itemListElement: PRODUCTS.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: productNode(p),
    })),
  };

  return [{ type: "application/ld+json", children: JSON.stringify(itemList) }];
}
